import React, { createContext, useContext, useState, useCallback } from "react";
import { showGlobalToast } from "../../utils/globalNotifications";

const SubscriptionContext = createContext();

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error(
      "useSubscription must be used within a SubscriptionProvider"
    );
  }
  return context;
};

const SUBSCRIPTION_TOAST_KEY = "wv_subscription_toast_shown";

export const SubscriptionProvider = ({ children }) => {
  const [isExpired, setIsExpired] = useState(false);
  const [hasShownToast, setHasShownToast] = useState(() => {
    try {
      return sessionStorage.getItem(SUBSCRIPTION_TOAST_KEY) === "true";
    } catch {
      return false;
    }
  });

  const markAsExpired = useCallback(() => {
    setIsExpired(true);

    const alreadyShown =
      sessionStorage.getItem(SUBSCRIPTION_TOAST_KEY) === "true";

    if (!alreadyShown && !hasShownToast) {
      setHasShownToast(true);
      try {
        sessionStorage.setItem(SUBSCRIPTION_TOAST_KEY, "true");
      } catch (e) {}
      try {
        showGlobalToast(
          "Your subscription has expired or is inactive. Please renew your subscription to continue using the service.",
          "error",
          6000
        );
      } catch (e) {}
      return true;
    }

    return false;
  }, [hasShownToast]);

  const reset = useCallback(() => {
    setIsExpired(false);
    setHasShownToast(false);
    try {
      sessionStorage.removeItem(SUBSCRIPTION_TOAST_KEY);
    } catch (e) {}
  }, []);

  const checkSubscriptionStatus = useCallback(
    (apiKeys) => {
      if (apiKeys && Array.isArray(apiKeys)) {
        const nonActiveStatuses = new Set([
          "expired",
          "inactive",
          "cancelled",
          "canceled",
          "past_due",
        ]);

        const hasExpiredCompany = apiKeys.some((key) => {
          const s = key.company?.subscription_status;
          if (!s) return false;
          return nonActiveStatuses.has(String(s).toLowerCase());
        });

        if (hasExpiredCompany && !isExpired) {
          return markAsExpired();
        } else if (!hasExpiredCompany && isExpired) {
          setIsExpired(false);
        }
      }
      return false;
    },
    [isExpired, markAsExpired]
  );

  const contextValue = {
    isExpired,
    hasShownToast,
    markAsExpired,
    reset,
    checkSubscriptionStatus,
  };

  return (
    <SubscriptionContext.Provider value={contextValue}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export default SubscriptionProvider;
